import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import './About.css';
import './Rent.css';

const RENT_TYPES = [
  {
    title: 'Website on Rent',
    description:
      'Business website, landing pages, admin panels — live on our stack, tailored to your brand and workflow.',
    icon: '🌐',
  },
  {
    title: 'Mobile App on Rent',
    description:
      'Android & iOS apps ready to use. We adjust screens, modules, and integrations as your operations grow.',
    icon: '📱',
  },
  {
    title: 'Custom Software on Rent',
    description:
      'CRM, dashboards, booking systems, and internal tools — rented instead of heavy one-time build cost.',
    icon: '⚙️',
  },
];

const PLAN_FEATURES = [
  'Regular maintenance & health checks',
  'Bug fixing & issue resolution',
  'Software updates & security patches',
  '24/7 uptime & performance monitoring',
  'Automated backups & data recovery',
  'Hosting, SSL & server management',
  'Database care & optimization',
  'Minor customizations (within agreed scope)',
  'Technical support via email, chat & call',
  'Analytics & usage reports',
  'API & third-party integration upkeep',
  'Documentation & handover support',
];

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly Plan',
    tagline: 'Flexible — pay as you go',
    priceNote: 'Custom quote based on your software',
    highlight: false,
    extras: [
      'Ideal for trials, seasonal business, or short campaigns',
      'Scale or pause with one month notice',
      'Same core care: maintenance, bugs, updates & monitoring',
    ],
  },
  {
    id: 'yearly',
    name: 'Yearly Plan',
    tagline: 'Best value — long-term partnership',
    priceNote: 'Save more vs monthly — pricing on request',
    highlight: true,
    extras: [
      'Priority support & faster response times',
      'More included customization hours per year',
      'Quarterly review of features & performance',
      'Better rates for hosting, updates & enhancements',
    ],
  },
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Tell us your need',
    text: 'Website, mobile app, or custom software — share your industry and must-have features.',
  },
  {
    step: '2',
    title: 'We tailor the product',
    text: 'GoToBusiness configures modules, design, and workflows to match how you actually work.',
  },
  {
    step: '3',
    title: 'Choose Monthly or Yearly',
    text: 'Pick a plan. We handle hosting, care, and ongoing improvements while you focus on business.',
  },
  {
    step: '4',
    title: 'Launch & stay supported',
    text: 'Go live with full maintenance, monitoring, and support — no separate IT team required.',
  },
];

const Rent = () => {
  const [introRef, introVisible] = useScrollAnimation(0.12);
  const [plansRef, plansVisible] = useScrollAnimation(0.1);

  usePageSEO(
    'Rent Website & Mobile App | Monthly & Yearly Plans - GoToBusiness',
    'Rent website, mobile application, or custom software from GoToBusiness. Monthly and yearly plans with maintenance, bug fixes, updates, monitoring, and support.'
  );

  return (
    <div className="about rent-page">
      <section className="about-hero rent-hero">
        <div className="container">
          <span className="rent-hero-badge">GoToBusiness — Software on Rent</span>
          <h1>Rent According to Your Need</h1>
          <p>
            Website, mobile application, or custom software — we provide ready solutions you can
            use and customize. Modify features, branding, and descriptions as your business evolves.
          </p>
          <div className="rent-hero-actions">
            <a href="#plans" className="btn">
              View Plans
            </a>
            <Link to="/contact" className="btn btn-secondary">
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <section
        ref={introRef}
        className={`section rent-intro ${introVisible ? 'animate' : ''}`}
      >
        <div className="container">
          <div className="rent-intro-grid">
            <div className="rent-intro-copy">
              <h2>Why rent instead of buy?</h2>
              <p>
                GoToBusiness gives you professional digital products without a large upfront
                investment. You get a working website or app, ongoing care, and the freedom to
                request changes as your operations change — all under a simple monthly or yearly
                plan.
              </p>
              <p>
                We modify the software, content, and modules to fit your process. You focus on
                customers; we handle technology, hosting, fixes, and updates.
              </p>
            </div>
            <div className="rent-types">
              {RENT_TYPES.map((type) => (
                <article key={type.title} className="card rent-type-card">
                  <span className="rent-type-icon" aria-hidden="true">
                    {type.icon}
                  </span>
                  <h3>{type.title}</h3>
                  <p>{type.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section rent-how" style={{ background: '#f8f9fa' }}>
        <div className="container">
          <h2 className="rent-section-title text-center">How it works</h2>
          <p className="rent-section-sub text-center">
            From first call to live product — simple and transparent
          </p>
          <div className="rent-steps">
            {HOW_IT_WORKS.map((item) => (
              <article key={item.step} className="card rent-step-card">
                <span className="rent-step-num">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="plans"
        ref={plansRef}
        className={`section rent-plans ${plansVisible ? 'animate' : ''}`}
      >
        <div className="container">
          <h2 className="rent-section-title text-center">Monthly &amp; Yearly Plans</h2>
          <p className="rent-section-sub text-center">
            Both plans include full software care. Yearly is best for stable, long-term use.
          </p>

          <div className="rent-plan-grid">
            {PLANS.map((plan) => (
              <article
                key={plan.id}
                className={`card rent-plan-card ${plan.highlight ? 'rent-plan-card--featured' : ''}`}
              >
                {plan.highlight && <span className="rent-plan-badge">Recommended</span>}
                <h3 className="rent-plan-name">{plan.name}</h3>
                <p className="rent-plan-tagline">{plan.tagline}</p>
                <p className="rent-plan-price">{plan.priceNote}</p>
                <ul className="rent-plan-extras">
                  {plan.extras.map((extra) => (
                    <li key={extra}>{extra}</li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`btn ${plan.highlight ? '' : 'btn-secondary'} rent-plan-cta`}
                >
                  Enquire — {plan.id === 'monthly' ? 'Monthly' : 'Yearly'}
                </Link>
              </article>
            ))}
          </div>

          <div className="card rent-features-block">
            <h3 className="rent-features-title">Included in every plan</h3>
            <p className="rent-features-lead">
              Everything you need to run rented software reliably — no hidden IT headaches.
            </p>
            <ul className="rent-features-list">
              {PLAN_FEATURES.map((feature) => (
                <li key={feature}>
                  <span className="rent-feature-check" aria-hidden="true">
                    ✓
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="section rent-cta">
        <div className="container rent-cta-inner">
          <h2>Ready to rent your website or app?</h2>
          <p>
            Tell us what you need — we will suggest the right product, plan, and timeline. Custom
            pricing based on features and scale.
          </p>
          <Link to="/contact" className="btn">
            Contact GoToBusiness
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Rent;
